import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow, differenceInDays, parseISO, startOfDay } from 'date-fns';
import { Calendar, User, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface MilestoneWithJob {
  id: string;
  name: string;
  due_date: string;
  is_completed: boolean;
  action_owner: 'client' | 'vendor';
  job_id: string;
  job_title: string;
  job_public_id: string;
  job_status: string;
}

interface UpcomingMilestonesProps {
  milestones: MilestoneWithJob[];
  loading: boolean;
}

const getUrgencyColor = (dueDate: string): string => {
  const daysUntil = differenceInDays(
    startOfDay(parseISO(dueDate)), 
    startOfDay(new Date())
  );
  
  if (daysUntil < 0) return 'border-l-destructive bg-destructive/5';
  if (daysUntil === 0) return 'border-l-destructive bg-destructive/5';
  if (daysUntil <= 3) return 'border-l-warning bg-warning/5';
  if (daysUntil <= 7) return 'border-l-success bg-success/5';
  return 'border-l-primary bg-primary/5';
};

const getTimeUntilText = (dueDate: string): string => {
  const daysUntil = differenceInDays(
    startOfDay(parseISO(dueDate)), 
    startOfDay(new Date())
  );
  
  if (daysUntil < 0) return 'Overdue';
  if (daysUntil === 0) return 'Due today';
  if (daysUntil === 1) return 'Due tomorrow';
  return `in ${daysUntil} days`;
};

export const UpcomingMilestones = ({ milestones, loading }: UpcomingMilestonesProps) => {
  const navigate = useNavigate();
  
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="border-l-4 border-muted p-4 rounded-md bg-muted/5">
            <Skeleton className="h-4 w-48 mb-2" />
            <Skeleton className="h-3 w-32 mb-3" />
            <div className="flex items-center space-x-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (milestones.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="mb-1">No upcoming milestones</p>
        <p className="text-xs">All caught up!</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {milestones.map((milestone) => (
        <button
          key={milestone.id}
          onClick={() => navigate(`/jobs/${milestone.job_public_id}/milestones`)}
          className={`w-full border-l-4 p-3 rounded-md transition-all hover:shadow-md text-left ${getUrgencyColor(milestone.due_date)}`}
        >
          <div className="space-y-1.5">
            <div className="flex items-start justify-between">
              <h4 className="font-semibold text-sm line-clamp-1">{milestone.name}</h4>
              {milestone.action_owner === 'client' ? (
                <Badge variant="outline" className="ml-2 shrink-0">
                  <User className="h-3 w-3 mr-1" />
                  Client
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-2 shrink-0">
                  <Building2 className="h-3 w-3 mr-1" />
                  Vendor
                </Badge>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground line-clamp-1">
              {milestone.job_title}
            </p>
            
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center space-x-1 text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>{format(parseISO(milestone.due_date), 'MMM d, yyyy')}</span>
              </div>
              <Badge 
                variant={differenceInDays(startOfDay(parseISO(milestone.due_date)), startOfDay(new Date())) < 0 ? 'destructive' : 'secondary'}
                className="text-xs"
              >
                {getTimeUntilText(milestone.due_date)}
              </Badge>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};
